/**
 * BATCH 22 PART 9 — Workflows 101-113: Marketo B2B Marketing Workflows
 * All workflows use Marketo as the marketing automation platform with
 * Salesforce CRM, Zoom (webinars), Slack (sales alerts), and OpenAI (scoring/analysis).
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ Salesforce helper ============

function salesforceQueryNode(
  id: string,
  label: string,
  y: number,
  soql: string,
  variables: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://yourinstance.salesforce.com/services/data/v59.0/query/?q=${encodeURIComponent(soql)}`,
        headers: [
          { key: "Authorization", value: "Bearer {salesforce_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables,
      },
    },
    x,
  );
}

function salesforceUpdateNode(
  id: string,
  label: string,
  y: number,
  object: string,
  recordId: string,
  fields: Array<{ key: string; value: string }>,
  variables: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "PATCH",
        url: `https://yourinstance.salesforce.com/services/data/v59.0/sobjects/${object}/${recordId}`,
        headers: [
          { key: "Authorization", value: "Bearer {salesforce_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: fields,
        variables,
      },
    },
    x,
  );
}

function marketoLookupNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  variables: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://yourinstance.mktorest.com/rest/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {marketo_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables,
      },
    },
    x,
  );
}

function zoomNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  variables: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.zoom.us/v2/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {zoom_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables,
      },
    },
    x,
  );
}

// ============ WORKFLOWS ============

export const ELITE_BATCH22_PART9: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─────────────────────────────────────────────────
  // 101. Lead Scoring Automation | Pattern K
  // ─────────────────────────────────────────────────
  "Lead Scoring Automation": {
    nodes: [
      trigger("Marketo: Lead activity webhook fires", "webhook_app", {
        source: "marketo",
        event: "lead_activity_logged",
        howItStarts:
          "Fires when a lead performs a tracked behavior in Marketo — page visit, email click, form fill, or content download — indicating purchase intent.",
        setup:
          "In Marketo, configure a webhook under Admin > Webhooks that POSTs to this endpoint on Smart List trigger activities (Visits Web Page, Clicks Link in Email, Fills Out Form).",
        inputFields: [],
      }),
      salesforceQueryNode(
        "2",
        "Salesforce: Fetch lead firmographic and fit data",
        280,
        "SELECT Id, Company, Industry, AnnualRevenue, NumberOfEmployees, LeadSource, Status, Rating FROM Lead WHERE Email = '{lead_email}'",
        [
          { name: "records[0].Id", customName: "sf_lead_id", type: "string" },
          {
            name: "records[0].Industry",
            customName: "lead_industry",
            type: "string",
          },
          {
            name: "records[0].AnnualRevenue",
            customName: "annual_revenue",
            type: "number",
          },
          {
            name: "records[0].NumberOfEmployees",
            customName: "employee_count",
            type: "number",
          },
          {
            name: "records[0].Rating",
            customName: "current_rating",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Compute behavior + fit score", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Behavior score from Marketo activity
let behaviorScore = 0;
if (activity_type === 'form_fill') behaviorScore += 25;
if (activity_type === 'email_click') behaviorScore += 10;
if (activity_type === 'page_visit' && page_url.includes('/pricing')) behaviorScore += 30;
if (activity_type === 'page_visit' && page_url.includes('/demo')) behaviorScore += 20;
if (activity_type === 'content_download') behaviorScore += 15;

// Fit score from Salesforce firmographic data
let fitScore = 0;
if (annual_revenue > 10000000) fitScore += 30;
else if (annual_revenue > 1000000) fitScore += 20;
if (employee_count > 200) fitScore += 20;
else if (employee_count > 50) fitScore += 10;
if (['Technology','Financial Services','Healthcare'].includes(lead_industry)) fitScore += 20;

const totalScore = behaviorScore + fitScore;
const grade = totalScore >= 70 ? 'A' : totalScore >= 50 ? 'B' : totalScore >= 30 ? 'C' : 'D';
return { total_score: totalScore, behavior_score: behaviorScore, fit_score: fitScore, lead_grade: grade };`,
          outputs: [
            { name: "total_score", type: "number" },
            { name: "behavior_score", type: "number" },
            { name: "fit_score", type: "number" },
            { name: "lead_grade", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Score >= 70 (MQL threshold)?", 760, {
        conditionDetails: {
          rules: [
            {
              field: "total_score",
              operator: "greaterThanOrEqual",
              value: "70",
            },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Alert sales — high-score lead ready for outreach",
        1000,
        "🔥 *New MQL Alert* — Lead: {lead_email} | Score: {total_score} (Behavior: {behavior_score}, Fit: {fit_score}) | Grade: {lead_grade} | Company: {lead_industry} | Revenue: ${annual_revenue} | Employees: {employee_count} — Route to SDR for immediate follow-up.",
      ),
      n("6", "delayAction", "Wait 4 hours for SDR response SLA", 1240, {
        delayDetails: { duration: 4, unit: "hours" },
      }),
      salesforceUpdateNode(
        "7",
        "Salesforce: Update lead score and routing status",
        1480,
        "Lead",
        "{sf_lead_id}",
        [
          { key: "Rating", value: "{lead_grade}" },
          { key: "Lead_Score__c", value: "{total_score}" },
          { key: "Status", value: "MQL - Routed" },
        ],
        [{ name: "id", customName: "updated_lead_id", type: "string" }],
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log scoring event and routing outcome",
        1720,
        "Lead_Scoring_Log",
        '[["{lead_email}","{lead_industry}","{annual_revenue}","{total_score}","{behavior_score}","{fit_score}","{lead_grade}","{activity_type}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically scores every Marketo lead activity against firmographic fit data from Salesforce, computes a composite behavior + fit score, alerts sales via Slack when leads cross the MQL threshold of 70 points, enforces a 4-hour SDR response SLA, updates the lead record in Salesforce with the computed grade, and logs every scoring event for pipeline analytics.",
  },

  // ─────────────────────────────────────────────────
  // 102. MQL Alert to Sales | Pattern K
  // ─────────────────────────────────────────────────
  "MQL Alert to Sales": {
    nodes: [
      trigger("Marketo: MQL status threshold reached", "webhook_app", {
        source: "marketo",
        event: "lead_status_changed_to_mql",
        howItStarts:
          "Fires when a lead's status changes to MQL in Marketo, indicating the lead has accumulated enough engagement and fit signals to warrant sales outreach.",
        setup:
          "In Marketo, create a Smart Campaign with trigger 'Lead Status is Changed' where New Status = 'MQL'. Add a webhook flow step that POSTs lead data to this endpoint.",
        inputFields: [],
      }),
      salesforceQueryNode(
        "2",
        "Salesforce: Enrich lead with account and owner context",
        280,
        "SELECT Id, Name, Company, Title, Phone, LeadSource, Owner.Name, Owner.Email, Lead_Score__c, ConvertedAccountId FROM Lead WHERE Email = '{lead_email}'",
        [
          { name: "records[0].Id", customName: "sf_lead_id", type: "string" },
          {
            name: "records[0].Company",
            customName: "company_name",
            type: "string",
          },
          {
            name: "records[0].Title",
            customName: "lead_title",
            type: "string",
          },
          {
            name: "records[0].Phone",
            customName: "lead_phone",
            type: "string",
          },
          {
            name: "records[0].Owner.Name",
            customName: "lead_owner_name",
            type: "string",
          },
          {
            name: "records[0].Owner.Email",
            customName: "lead_owner_email",
            type: "string",
          },
          {
            name: "records[0].Lead_Score__c",
            customName: "lead_score",
            type: "number",
          },
        ],
      ),
      n(
        "3",
        "runCode",
        "Determine SDR routing by territory and score tier",
        520,
        {
          codeDetails: {
            language: "javascript",
            code: `// Route based on lead score tier and company size
const scoreTier = lead_score >= 85 ? 'hot' : lead_score >= 70 ? 'warm' : 'standard';
const slaMinutes = scoreTier === 'hot' ? 30 : scoreTier === 'warm' ? 120 : 480;
const priority = scoreTier === 'hot' ? 'P1 - Immediate' : scoreTier === 'warm' ? 'P2 - Same Day' : 'P3 - Next Day';
const assignedSdr = lead_owner_email || 'sdr-team@company.com';
return { score_tier: scoreTier, sla_minutes: slaMinutes, priority: priority, assigned_sdr: assignedSdr };`,
            outputs: [
              { name: "score_tier", type: "string" },
              { name: "sla_minutes", type: "number" },
              { name: "priority", type: "string" },
              { name: "assigned_sdr", type: "string" },
            ],
          },
        },
      ),
      n("4", "condition", "Score tier is hot (>= 85)?", 760, {
        conditionDetails: {
          rules: [{ field: "score_tier", operator: "equals", value: "hot" }],
        },
      }),
      slackNode(
        "5",
        "Slack: Urgent alert — hot MQL needs immediate SDR contact",
        1000,
        "🚨 *HOT MQL — {priority}* 🚨\\nLead: {lead_name} ({lead_email})\\nTitle: {lead_title} | Company: {company_name}\\nScore: {lead_score} | Phone: {lead_phone}\\nOwner: {lead_owner_name}\\nSLA: {sla_minutes} min — Contact immediately!",
      ),
      n("6", "delayAction", "Wait for SDR SLA window", 1240, {
        delayDetails: { duration: 2, unit: "hours" },
      }),
      salesforceUpdateNode(
        "7",
        "Salesforce: Create SDR task and update lead status",
        1480,
        "Lead",
        "{sf_lead_id}",
        [
          { key: "Status", value: "MQL - SDR Assigned" },
          { key: "MQL_Date__c", value: "{timestamp}" },
          { key: "MQL_Priority__c", value: "{priority}" },
        ],
        [{ name: "id", customName: "updated_lead_id", type: "string" }],
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log MQL handoff for SLA tracking",
        1720,
        "MQL_Handoff_Log",
        '[["{lead_email}","{company_name}","{lead_score}","{score_tier}","{priority}","{assigned_sdr}","{sla_minutes}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Instantly notifies sales when a Marketo lead crosses the MQL threshold, enriches the lead with Salesforce account context, routes to the appropriate SDR based on score tier and territory, sends an urgent Slack alert for hot leads with a 30-minute SLA, creates a follow-up task in Salesforce, and logs every MQL handoff for SLA compliance reporting.",
  },

  // ─────────────────────────────────────────────────
  // 103. Lead Recycle Nurture | Pattern G
  // ─────────────────────────────────────────────────
  "Lead Recycle Nurture": {
    nodes: [
      trigger(
        "Marketo: Sales-rejected lead returned to marketing",
        "schedule",
        {
          schedule: "Every day at 8:00 AM UTC",
          howItStarts:
            "Runs daily to pick up leads that sales has rejected or recycled back to marketing. These are leads that were previously MQL but did not convert during the sales cycle — they need re-nurturing before re-qualification.",
          setup:
            "Create a Marketo Smart List filtering leads where Status = 'Recycled' and Recycle_Date__c is within the last 24 hours. Schedule this workflow to run daily.",
          inputFields: [],
        },
      ),
      salesforceQueryNode(
        "2",
        "Salesforce: Fetch recycled lead history and rejection reason",
        280,
        "SELECT Id, Email, Name, Company, Status, Recycle_Reason__c, Last_MQL_Date__c, Lead_Score__c, Owner.Name FROM Lead WHERE Status = 'Recycled' AND Recycle_Date__c = LAST_N_DAYS:1",
        [
          { name: "records[0].Id", customName: "sf_lead_id", type: "string" },
          {
            name: "records[0].Email",
            customName: "lead_email",
            type: "string",
          },
          { name: "records[0].Name", customName: "lead_name", type: "string" },
          {
            name: "records[0].Recycle_Reason__c",
            customName: "recycle_reason",
            type: "string",
          },
          {
            name: "records[0].Lead_Score__c",
            customName: "previous_score",
            type: "number",
          },
          {
            name: "records[0].Company",
            customName: "company_name",
            type: "string",
          },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Analyze rejection reason and recommend nurture path",
        520,
        "You are a B2B demand gen strategist. Given a lead's recycle reason and history, recommend the best re-nurture approach. Return JSON with fields: nurture_track (string: timing|budget|authority|need), recommended_content (string), requalification_timeline_days (number), personalized_hook (string).",
        "Lead: {lead_name} at {company_name}. Recycle reason: {recycle_reason}. Previous score: {previous_score}. Determine the best nurture track and content to re-engage this lead.",
        [
          { field: "nurture_track", as: "nurture_track", type: "string" },
          {
            field: "recommended_content",
            as: "recommended_content",
            type: "string",
          },
          {
            field: "requalification_timeline_days",
            as: "requalify_days",
            type: "number",
          },
          {
            field: "personalized_hook",
            as: "personalized_hook",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Recycle reason is timing (not lost)?", 760, {
        conditionDetails: {
          rules: [
            { field: "nurture_track", operator: "equals", value: "timing" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Re-nurture with value-driven content relevant to objection",
        1000,
        "{lead_email}",
        "New resource for {company_name}: {recommended_content}",
        "Hi {lead_name},\\n\\n{personalized_hook}\\n\\nWe recently published a resource that addresses exactly what teams like yours at {company_name} are working through:\\n\\n📄 {recommended_content}\\n\\nNo commitment needed — just thought you'd find it valuable as you evaluate your options.\\n\\nBest,\\nThe Marketing Team",
      ),
      n("6", "delayAction", "Wait 14 days for re-engagement signals", 1240, {
        delayDetails: { duration: 14, unit: "days" },
      }),
      n("7", "condition", "Lead re-engaged (opened or clicked)?", 1480, {
        conditionDetails: {
          rules: [{ field: "email_opened", operator: "equals", value: "true" }],
        },
      }),
      emailNode(
        "8",
        "Email: Progressive content with soft re-qualification CTA",
        1720,
        "{lead_email}",
        "{lead_name}, see how {company_name}'s peers are solving this",
        "Hi {lead_name},\\n\\nSince you explored our {recommended_content}, I wanted to share how companies similar to {company_name} have tackled this challenge:\\n\\n📊 Case Study: How a {lead_industry} company achieved 3x ROI\\n\\nWould it be worth a quick 15-minute call to see if the timing is better now? No pressure — happy to share more resources either way.\\n\\n[Schedule a Quick Chat →]\\n\\nBest,\\nThe Marketing Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Automatically nurtures sales-rejected leads back toward re-qualification by analyzing the rejection reason with AI, selecting the optimal nurture track (timing, budget, authority, or need), sending personalized value-driven content that addresses the specific objection, waiting 14 days for re-engagement signals, and following up with progressive case study content and a soft re-qualification CTA.",
  },

  // ─────────────────────────────────────────────────
  // 104. Webinar Promotion Program | Pattern I
  // ─────────────────────────────────────────────────
  "Webinar Promotion Program": {
    nodes: [
      trigger("Marketo: New webinar created for promotion", "webhook_app", {
        source: "marketo",
        event: "program_created_webinar",
        howItStarts:
          "Fires when a new webinar program is created in Marketo, triggering the automated promotion sequence to drive registrations from qualified segments.",
        setup:
          "In Marketo, set up a webhook on program creation for Webinar program types. Include the webinar ID, topic, date, speaker, and target segment info in the payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Generate compelling webinar promotion copy",
        280,
        "You are a B2B webinar marketing copywriter. Given webinar details, generate promotion email copy. Return JSON with fields: subject_line (string), preview_text (string), email_body (string), value_propositions (string with 3 bullet points), urgency_hook (string).",
        "Webinar: {webinar_title}. Speaker: {speaker_name}, {speaker_title}. Date: {webinar_date}. Topic: {webinar_topic}. Target audience: {target_segment}. Generate a compelling invitation email.",
        [
          { field: "subject_line", as: "promo_subject", type: "string" },
          { field: "email_body", as: "promo_body", type: "string" },
          { field: "value_propositions", as: "value_props", type: "string" },
          { field: "urgency_hook", as: "urgency_hook", type: "string" },
        ],
      ),
      marketoLookupNode(
        "3",
        "Marketo: Pull target segment members for webinar topic",
        520,
        "leads.json?filterType=smartListId&filterValues={target_smart_list_id}&fields=email,firstName,lastName,company,title",
        [
          {
            name: "result[0].email",
            customName: "segment_lead_email",
            type: "string",
          },
          {
            name: "result[0].firstName",
            customName: "lead_first_name",
            type: "string",
          },
          {
            name: "result[0].company",
            customName: "lead_company",
            type: "string",
          },
          {
            name: "totalResultCount",
            customName: "segment_size",
            type: "number",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Segment has engaged leads (opened email in last 90 days)?",
        760,
        {
          conditionDetails: {
            rules: [
              { field: "segment_size", operator: "greaterThan", value: "0" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Send webinar invitation with registration link",
        1000,
        "{segment_lead_email}",
        "{promo_subject}",
        "Hi {lead_first_name},\\n\\n{promo_body}\\n\\n🎯 What you'll learn:\\n{value_props}\\n\\n📅 Date: {webinar_date}\\n🎤 Speaker: {speaker_name}, {speaker_title}\\n\\n[Register Now — Limited Spots →]\\n\\n{urgency_hook}\\n\\nBest,\\nThe Events Team",
      ),
      n("6", "delayAction", "Wait 4 days before reminder wave", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),
      n("7", "condition", "Lead has not yet registered?", 1480, {
        conditionDetails: {
          rules: [
            {
              field: "registration_status",
              operator: "notEquals",
              value: "registered",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Registration reminder with social proof and scarcity",
        1720,
        "{segment_lead_email}",
        "Filling up fast: {webinar_title} — {lead_first_name}, save your spot",
        "Hi {lead_first_name},\\n\\nA quick heads up — our upcoming webinar on {webinar_topic} is filling up fast, with {registration_count}+ professionals already registered.\\n\\n🎤 {speaker_name} will share actionable strategies that teams at companies like {lead_company} can implement immediately.\\n\\n📅 {webinar_date}\\n\\nDon't miss out — [Register Before It's Full →]\\n\\nSee you there,\\nThe Events Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Promotes new webinars by generating AI-powered invitation copy tailored to the topic and audience, pulling qualified segment members from Marketo, sending personalized invitations with speaker details and value propositions, waiting 4 days for initial registrations, then following up with non-registrants using social proof and scarcity to maximize attendance rates.",
  },

  // ─────────────────────────────────────────────────
  // 105. Webinar Registration Reminder | Pattern I
  // ─────────────────────────────────────────────────
  "Webinar Registration Reminder": {
    nodes: [
      trigger("Marketo: Webinar registration confirmed", "webhook_app", {
        source: "marketo",
        event: "webinar_registration_completed",
        howItStarts:
          "Fires when a lead completes webinar registration through a Marketo landing page form, kicking off the pre-webinar reminder sequence to maximize attendance rates.",
        setup:
          "In Marketo, create a Smart Campaign triggered by 'Fills Out Form' on the webinar registration landing page. Add a webhook flow step to POST registrant data to this endpoint.",
        inputFields: [],
      }),
      zoomNode(
        "2",
        "Zoom: Verify registration and get join link",
        280,
        "GET",
        "webinars/{webinar_id}/registrants?status=approved",
        [],
        [
          {
            name: "registrants[0].join_url",
            customName: "zoom_join_url",
            type: "string",
          },
          {
            name: "registrants[0].email",
            customName: "registrant_email",
            type: "string",
          },
          {
            name: "registrants[0].first_name",
            customName: "registrant_first_name",
            type: "string",
          },
        ],
      ),
      marketoLookupNode(
        "3",
        "Marketo: Fetch registrant lifecycle stage and engagement history",
        520,
        "leads.json?filterType=email&filterValues={registrant_email}&fields=email,firstName,company,lifecycleStage,engagementScore",
        [
          {
            name: "result[0].company",
            customName: "registrant_company",
            type: "string",
          },
          {
            name: "result[0].lifecycleStage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "result[0].engagementScore",
            customName: "engagement_score",
            type: "number",
          },
        ],
      ),
      n("4", "condition", "Webinar is tomorrow (within 24 hours)?", 760, {
        conditionDetails: {
          rules: [
            {
              field: "hours_until_webinar",
              operator: "lessThanOrEqual",
              value: "24",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Day-before reminder with agenda and join link",
        1000,
        "{registrant_email}",
        "Tomorrow: {webinar_title} — here's your join link, {registrant_first_name}",
        "Hi {registrant_first_name},\\n\\nJust a friendly reminder — the {webinar_title} webinar is tomorrow!\\n\\n📅 Date: {webinar_date}\\n⏰ Time: {webinar_time}\\n🔗 Your personal join link: {zoom_join_url}\\n\\n📋 Agenda Preview:\\n• Industry trends and challenges\\n• Live demo and actionable strategies\\n• Q&A with {speaker_name}\\n\\n💡 Pro tip: Add this to your calendar now so you don't miss it.\\n\\nSee you tomorrow!\\nThe Events Team",
      ),
      n("6", "delayAction", "Wait until 1 hour before webinar", 1240, {
        delayDetails: { duration: 23, unit: "hours" },
      }),
      n("7", "condition", "Registrant has not joined yet?", 1480, {
        conditionDetails: {
          rules: [
            {
              field: "attendance_status",
              operator: "notEquals",
              value: "joined",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Starting now — one-click join link reminder",
        1720,
        "{registrant_email}",
        "Starting in 1 hour: {webinar_title} — join now!",
        "Hi {registrant_first_name},\\n\\n⏰ The {webinar_title} webinar starts in just 1 hour!\\n\\nJoin with one click:\\n🔗 {zoom_join_url}\\n\\nWe've got a packed session with {speaker_name} — don't miss the live Q&A at the end.\\n\\nSee you there!\\nThe Events Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Maximizes webinar attendance by sending a well-timed reminder sequence to registrants. Verifies registration in Zoom, pulls the personalized join link, sends a day-before reminder with the full agenda and calendar prompt, then sends a final one-hour-before reminder with a one-click join link to minimize no-shows.",
  },

  // ─────────────────────────────────────────────────
  // 106. Webinar Attendee Follow-Up | Pattern I
  // ─────────────────────────────────────────────────
  "Webinar Attendee Follow-Up": {
    nodes: [
      trigger("Zoom: Webinar ended — attendee data available", "webhook_app", {
        source: "zoom",
        event: "webinar.ended",
        howItStarts:
          "Fires when a Zoom webinar ends, triggering follow-up for all attendees with recording, summary, and next-step content based on engagement level.",
        setup:
          "In Zoom Marketplace, configure the 'webinar.ended' event webhook to POST attendee data to this endpoint. Ensure the Zoom-Marketo integration syncs attendance status.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Generate webinar summary and key takeaways",
        280,
        "You are a B2B content marketing specialist. Given webinar details and topics covered, generate a concise summary. Return JSON with fields: summary (string, 2-3 sentences), key_takeaways (string, 3 bullet points), next_resource (string, recommended related content), cta_text (string).",
        "Webinar: {webinar_title}. Speaker: {speaker_name}. Topics covered: {webinar_topics}. Duration: {webinar_duration} minutes. Generate a follow-up summary for attendees.",
        [
          { field: "summary", as: "webinar_summary", type: "string" },
          { field: "key_takeaways", as: "key_takeaways", type: "string" },
          { field: "next_resource", as: "next_resource", type: "string" },
          { field: "cta_text", as: "cta_text", type: "string" },
        ],
      ),
      marketoLookupNode(
        "3",
        "Marketo: Fetch attendee profile and engagement score",
        520,
        "leads.json?filterType=email&filterValues={attendee_email}&fields=email,firstName,company,lifecycleStage,engagementScore,title",
        [
          {
            name: "result[0].firstName",
            customName: "attendee_first_name",
            type: "string",
          },
          {
            name: "result[0].company",
            customName: "attendee_company",
            type: "string",
          },
          {
            name: "result[0].lifecycleStage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "result[0].engagementScore",
            customName: "engagement_score",
            type: "number",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Attendee engaged actively (stayed > 75% of session)?",
        760,
        {
          conditionDetails: {
            rules: [
              {
                field: "attendance_duration_pct",
                operator: "greaterThan",
                value: "75",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Recording + summary + next content for engaged attendees",
        1000,
        "{attendee_email}",
        "Thanks for joining {webinar_title} — here's your recording + next steps",
        "Hi {attendee_first_name},\\n\\nThank you for attending {webinar_title}! Here's everything you need:\\n\\n🎥 Watch the recording: [View Recording →]\\n\\n📝 Key Takeaways:\\n{key_takeaways}\\n\\n{webinar_summary}\\n\\n📚 Recommended Next Resource: {next_resource}\\n\\n{cta_text}\\n\\nBest,\\nThe Events Team",
      ),
      n("6", "delayAction", "Wait 4 days for content consumption", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),
      n("7", "condition", "Attendee consumed follow-up content?", 1480, {
        conditionDetails: {
          rules: [
            {
              field: "followup_content_viewed",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Progressive content — case study and meeting invite",
        1720,
        "{attendee_email}",
        "{attendee_first_name}, see how {attendee_company}'s peers are applying these strategies",
        "Hi {attendee_first_name},\\n\\nSince you found our {webinar_title} session valuable, I thought you'd appreciate seeing these strategies in action:\\n\\n📊 Case Study: How a company like {attendee_company} achieved measurable results using the approach {speaker_name} outlined.\\n\\n[Read the Case Study →]\\n\\nWant to explore how this could work for your team? I'd love to set up a quick 15-minute call.\\n\\n[Book a Time →]\\n\\nBest,\\nThe Events Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Follows up with webinar attendees by generating an AI-powered session summary with key takeaways, segmenting by engagement level (stayed more than 75% vs. partial), sending the recording with personalized next-step content for active attendees, waiting 4 days for content consumption, then advancing engaged attendees with a case study and meeting invite to convert interest into pipeline.",
  },

  // ─────────────────────────────────────────────────
  // 107. Webinar No-Show Nurture | Pattern I
  // ─────────────────────────────────────────────────
  "Webinar No-Show Nurture": {
    nodes: [
      trigger(
        "Zoom: Webinar ended — no-show registrants identified",
        "webhook_app",
        {
          source: "zoom",
          event: "webinar.ended",
          howItStarts:
            "Fires after a webinar ends, targeting registrants who did not attend. These leads showed intent by registering but need re-engagement with the recording and alternative content paths.",
          setup:
            "In Zoom, configure the webinar.ended webhook. Cross-reference registrants against attendees to identify no-shows. Ensure Marketo program status is updated to 'No Show'.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Craft empathetic no-show message with content alternatives",
        280,
        "You are a B2B event marketer. Generate a no-show follow-up email that is empathetic (not guilt-tripping), offers the recording, and suggests alternative ways to get the value. Return JSON: subject (string), body (string), alternative_content (string), micro_cta (string).",
        "Webinar: {webinar_title}. Speaker: {speaker_name}. Topic: {webinar_topic}. Registrant name: {registrant_first_name}. Generate a warm no-show follow-up.",
        [
          { field: "subject", as: "noshow_subject", type: "string" },
          { field: "body", as: "noshow_body", type: "string" },
          { field: "alternative_content", as: "alt_content", type: "string" },
          { field: "micro_cta", as: "micro_cta", type: "string" },
        ],
      ),
      marketoLookupNode(
        "3",
        "Marketo: Check no-show registrant's overall engagement history",
        520,
        "leads.json?filterType=email&filterValues={registrant_email}&fields=email,firstName,company,engagementScore,lastActivityDate",
        [
          {
            name: "result[0].firstName",
            customName: "registrant_first_name",
            type: "string",
          },
          {
            name: "result[0].company",
            customName: "registrant_company",
            type: "string",
          },
          {
            name: "result[0].engagementScore",
            customName: "engagement_score",
            type: "number",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Registrant has prior engagement (score > 30)?",
        760,
        {
          conditionDetails: {
            rules: [
              {
                field: "engagement_score",
                operator: "greaterThan",
                value: "30",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Missed the live session — here's the recording and summary",
        1000,
        "{registrant_email}",
        "{noshow_subject}",
        "Hi {registrant_first_name},\\n\\n{noshow_body}\\n\\n🎥 Watch at your own pace: [View the Full Recording →]\\n\\n⏱️ Short on time? Here's a 5-minute highlight reel covering the key points.\\n\\n📚 Alternative: {alt_content}\\n\\n{micro_cta}\\n\\nBest,\\nThe Events Team",
      ),
      n("6", "delayAction", "Wait 5 days for recording engagement", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Watched recording or clicked content?", 1480, {
        conditionDetails: {
          rules: [
            { field: "recording_viewed", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Re-engage with next webinar or related deep-dive content",
        1720,
        "{registrant_email}",
        "{registrant_first_name}, we have more on {webinar_topic} for you",
        "Hi {registrant_first_name},\\n\\nGlad you caught the {webinar_title} recording! Since you're interested in {webinar_topic}, here are two ways to go deeper:\\n\\n📅 Upcoming Webinar: {next_webinar_title} — [Register Now →]\\n\\n📄 Deep Dive Guide: The complete playbook on {webinar_topic} with step-by-step implementation guidance.\\n\\n[Download the Guide →]\\n\\nBest,\\nThe Events Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Recovers webinar no-show registrants with an empathetic, AI-generated follow-up that avoids guilt and instead offers the on-demand recording plus a highlight reel for time-pressed leads. Segments by prior engagement to prioritize high-value no-shows, waits 5 days for recording consumption, then re-engages viewers with the next related webinar or deep-dive content to keep them in the funnel.",
  },

  // ─────────────────────────────────────────────────
  // 108. On-Demand Webinar Nurture | Pattern I
  // ─────────────────────────────────────────────────
  "On-Demand Webinar Nurture": {
    nodes: [
      trigger("Marketo: On-demand webinar viewed", "webhook_app", {
        source: "marketo",
        event: "on_demand_webinar_viewed",
        howItStarts:
          "Fires when a lead watches an on-demand webinar recording through a Marketo-tracked landing page, indicating topic-level interest that can be nurtured toward a sales conversation.",
        setup:
          "In Marketo, create a Smart Campaign triggered by 'Visits Web Page' on the on-demand webinar landing page with a minimum view duration of 5 minutes. Webhook POSTs viewer data.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze webinar topic and recommend progressive content path",
        280,
        "You are a B2B content strategist. Given the on-demand webinar topic and viewer details, recommend a progressive content nurture path. Return JSON: topic_category (string), next_content_piece (string), content_description (string), progression_cta (string), buyer_stage_estimate (string).",
        "On-demand webinar: {webinar_title}. Topic: {webinar_topic}. Viewer: {viewer_first_name} at {viewer_company}, title: {viewer_title}. View duration: {view_duration_minutes} minutes of {total_duration_minutes}. Recommend next content.",
        [
          { field: "topic_category", as: "topic_category", type: "string" },
          { field: "next_content_piece", as: "next_content", type: "string" },
          {
            field: "content_description",
            as: "content_description",
            type: "string",
          },
          { field: "progression_cta", as: "progression_cta", type: "string" },
          { field: "buyer_stage_estimate", as: "buyer_stage", type: "string" },
        ],
      ),
      marketoLookupNode(
        "3",
        "Marketo: Fetch viewer content consumption history",
        520,
        "leads.json?filterType=email&filterValues={viewer_email}&fields=email,firstName,company,title,engagementScore,contentDownloads,webinarsAttended",
        [
          {
            name: "result[0].firstName",
            customName: "viewer_first_name",
            type: "string",
          },
          {
            name: "result[0].company",
            customName: "viewer_company",
            type: "string",
          },
          {
            name: "result[0].engagementScore",
            customName: "engagement_score",
            type: "number",
          },
          {
            name: "result[0].contentDownloads",
            customName: "content_downloads",
            type: "number",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Viewer watched > 60% of recording (high engagement)?",
        760,
        {
          conditionDetails: {
            rules: [
              {
                field: "view_completion_pct",
                operator: "greaterThan",
                value: "60",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Related content based on webinar topic and viewer stage",
        1000,
        "{viewer_email}",
        "Liked {webinar_title}? Here's your next step, {viewer_first_name}",
        "Hi {viewer_first_name},\\n\\nThanks for watching {webinar_title} — great choice!\\n\\nBased on your interest in {topic_category}, here's the logical next resource:\\n\\n📄 {next_content}: {content_description}\\n\\n[Access Now →]\\n\\n{progression_cta}\\n\\nBest,\\nThe Content Team",
      ),
      n("6", "delayAction", "Wait 5 days for content consumption", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Consumed recommended content?", 1480, {
        conditionDetails: {
          rules: [
            {
              field: "next_content_consumed",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Progressive nurture — ROI data and consultation offer",
        1720,
        "{viewer_email}",
        "{viewer_first_name}, the ROI data behind {topic_category} strategies",
        "Hi {viewer_first_name},\\n\\nYou've been diving deep into {topic_category} — here's the data to make the business case:\\n\\n📊 ROI Report: Companies implementing these strategies see an average of 2.5x improvement in key metrics.\\n\\n[Download the ROI Report →]\\n\\nReady to explore what this could look like for {viewer_company}? Our solutions team can walk you through a personalized assessment.\\n\\n[Schedule a 15-Min Assessment →]\\n\\nBest,\\nThe Content Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Nurtures on-demand webinar viewers through a progressive content path by analyzing the topic with AI to recommend the ideal next resource, segmenting by viewing depth (above or below 60% completion), sending related content for engaged viewers, waiting 5 days, then advancing those who consumed follow-up content toward a consultation with ROI data to convert interest into pipeline.",
  },

  // ─────────────────────────────────────────────────
  // 109. Content Download Nurture | Pattern I
  // ─────────────────────────────────────────────────
  "Content Download Nurture": {
    nodes: [
      trigger("Marketo: Content asset downloaded via form", "webhook_app", {
        source: "marketo",
        event: "form_fill_content_download",
        howItStarts:
          "Fires when a lead downloads a gated content asset (whitepaper, eBook, report, guide) through a Marketo form, indicating topic-level interest to nurture toward a sales-ready state.",
        setup:
          "In Marketo, create a Smart Campaign triggered by 'Fills Out Form' on content download landing pages. The webhook POSTs lead info, asset name, and content category.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Map content topic to progressive nurture sequence",
        280,
        "You are a B2B demand gen strategist. Given the downloaded content asset, recommend the progressive nurture sequence. Return JSON: content_category (string), follow_up_content (string), follow_up_description (string), case_study_match (string), buyer_journey_stage (string: awareness|consideration|decision).",
        "Downloaded asset: {asset_name}. Content category: {content_category}. Lead: {lead_first_name} at {lead_company}, role: {lead_title}. Map this to the right nurture path.",
        [
          { field: "content_category", as: "mapped_category", type: "string" },
          {
            field: "follow_up_content",
            as: "follow_up_content",
            type: "string",
          },
          {
            field: "follow_up_description",
            as: "follow_up_desc",
            type: "string",
          },
          { field: "case_study_match", as: "case_study", type: "string" },
          { field: "buyer_journey_stage", as: "buyer_stage", type: "string" },
        ],
      ),
      marketoLookupNode(
        "3",
        "Marketo: Check lead's content history and lifecycle stage",
        520,
        "leads.json?filterType=email&filterValues={lead_email}&fields=email,firstName,company,title,lifecycleStage,engagementScore,contentDownloads",
        [
          {
            name: "result[0].firstName",
            customName: "lead_first_name",
            type: "string",
          },
          {
            name: "result[0].company",
            customName: "lead_company",
            type: "string",
          },
          {
            name: "result[0].lifecycleStage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "result[0].contentDownloads",
            customName: "prior_downloads",
            type: "number",
          },
        ],
      ),
      n("4", "condition", "Lead is in consideration or decision stage?", 760, {
        conditionDetails: {
          rules: [
            { field: "buyer_stage", operator: "notEquals", value: "awareness" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Thank you with download link and related content recommendation",
        1000,
        "{lead_email}",
        "Your {asset_name} is ready, {lead_first_name} — plus a bonus resource",
        "Hi {lead_first_name},\\n\\nThanks for downloading {asset_name}! Here's your direct link:\\n\\n📄 [Download {asset_name} →]\\n\\nSince you're exploring {mapped_category}, you might also find this valuable:\\n\\n📚 {follow_up_content}: {follow_up_desc}\\n\\n[Access Now →]\\n\\nEnjoy the read!\\nThe Content Team",
      ),
      n("6", "delayAction", "Wait 4 days for content consumption", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),
      n("7", "condition", "Lead engaged with follow-up content?", 1480, {
        conditionDetails: {
          rules: [
            { field: "follow_up_engaged", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Progressive case study and soft demo CTA",
        1720,
        "{lead_email}",
        "How {case_study} succeeded with {mapped_category} — {lead_first_name}",
        "Hi {lead_first_name},\\n\\nSince you've been exploring {mapped_category}, I thought you'd appreciate seeing these principles in action:\\n\\n📊 {case_study}\\n\\nThis case study shows measurable results that teams like yours at {lead_company} can replicate.\\n\\n[Read the Full Case Study →]\\n\\nWant to see how this could work for {lead_company}? Happy to set up a quick walkthrough.\\n\\n[Book a 15-Min Demo →]\\n\\nBest,\\nThe Content Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Nurtures content downloaders through a progressive sequence by mapping the downloaded asset to a content category with AI, delivering the asset with a related follow-up resource, segmenting by buyer journey stage, waiting 4 days for consumption signals, then advancing engaged leads with a matched case study and a soft demo CTA to convert content interest into sales conversations.",
  },

  // ─────────────────────────────────────────────────
  // 110. Product Launch Email Series | Pattern I
  // ─────────────────────────────────────────────────
  "Product Launch Email Series": {
    nodes: [
      trigger("Marketo: Product launch campaign activated", "webhook_app", {
        source: "marketo",
        event: "program_activated_product_launch",
        howItStarts:
          "Fires when a product launch email program is activated in Marketo, triggering the multi-touch announcement sequence to drive awareness, education, and trial signups for the new product.",
        setup:
          "In Marketo, create a Program of type 'Email' for the product launch. Configure a webhook on program activation that POSTs the product name, features, target segment, and launch date.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Generate product launch announcement and feature highlights",
        280,
        "You are a B2B product marketing copywriter. Given new product details, generate launch email copy. Return JSON: announcement_subject (string), announcement_body (string), feature_highlights (string with 3 key features), differentiator (string), trial_cta (string).",
        "Product: {product_name}. Key features: {product_features}. Target audience: {target_segment}. Competitive advantage: {competitive_edge}. Launch date: {launch_date}. Generate compelling launch announcement copy.",
        [
          {
            field: "announcement_subject",
            as: "launch_subject",
            type: "string",
          },
          { field: "announcement_body", as: "launch_body", type: "string" },
          {
            field: "feature_highlights",
            as: "feature_highlights",
            type: "string",
          },
          { field: "differentiator", as: "differentiator", type: "string" },
          { field: "trial_cta", as: "trial_cta", type: "string" },
        ],
      ),
      marketoLookupNode(
        "3",
        "Marketo: Pull target segment for product launch",
        520,
        "leads.json?filterType=smartListId&filterValues={launch_segment_id}&fields=email,firstName,company,title,productInterest,lifecycleStage",
        [
          { name: "result[0].email", customName: "lead_email", type: "string" },
          {
            name: "result[0].firstName",
            customName: "lead_first_name",
            type: "string",
          },
          {
            name: "result[0].company",
            customName: "lead_company",
            type: "string",
          },
          {
            name: "totalResultCount",
            customName: "segment_count",
            type: "number",
          },
        ],
      ),
      n("4", "condition", "Lead has shown prior product interest?", 760, {
        conditionDetails: {
          rules: [
            {
              field: "product_interest_match",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Product launch announcement with feature highlights",
        1000,
        "{lead_email}",
        "{launch_subject}",
        "Hi {lead_first_name},\\n\\n{launch_body}\\n\\n🚀 Key Features:\\n{feature_highlights}\\n\\n💡 Why it matters: {differentiator}\\n\\n{trial_cta}\\n\\n[Start Your Free Trial →]\\n\\nBest,\\nThe Product Team",
      ),
      n("6", "delayAction", "Wait 3 days before deep-dive follow-up", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Opened announcement but hasn't started trial?",
        1480,
        {
          conditionDetails: {
            rules: [
              { field: "trial_started", operator: "equals", value: "false" },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Deep-dive feature walkthrough and trial incentive",
        1720,
        "{lead_email}",
        "{lead_first_name}, see {product_name} in action — 3-minute walkthrough",
        "Hi {lead_first_name},\\n\\nWanted to share a quick walkthrough of {product_name} that shows exactly how teams like yours at {lead_company} can get value from day one:\\n\\n🎥 [Watch the 3-Minute Product Tour →]\\n\\nHere's what customers are already achieving:\\n• 40% reduction in manual effort\\n• Real-time visibility into key metrics\\n• Seamless integration with your existing stack\\n\\nStart your free trial today — no credit card required.\\n\\n[Try {product_name} Free →]\\n\\nBest,\\nThe Product Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Orchestrates a multi-touch product launch email series by generating AI-powered announcement copy with feature highlights and differentiation, targeting the right segment from Marketo, prioritizing leads with prior product interest, sending the launch announcement with trial CTA, then following up 3 days later with a product tour video and customer proof for those who opened but haven't started a trial.",
  },

  // ─────────────────────────────────────────────────
  // 111. Trial Onboarding Nurture | Pattern A
  // ─────────────────────────────────────────────────
  "Trial Onboarding Nurture": {
    nodes: [
      trigger("Marketo: Free trial started", "webhook_app", {
        source: "marketo",
        event: "trial_signup_completed",
        howItStarts:
          "Fires when a new user starts a free trial, kicking off the onboarding nurture sequence that guides them through setup milestones to drive activation and conversion to paid.",
        setup:
          "In Marketo, trigger a webhook when a lead's Trial_Status__c changes to 'Active'. POST the lead email, name, company, trial start date, and plan tier.",
        inputFields: [],
      }),
      salesforceQueryNode(
        "2",
        "Salesforce: Fetch trial account context and owner",
        280,
        "SELECT Id, Name, Email, Company, Title, Trial_Start_Date__c, Plan_Tier__c, Owner.Name, Owner.Email FROM Lead WHERE Email = '{trial_email}'",
        [
          { name: "records[0].Id", customName: "sf_lead_id", type: "string" },
          {
            name: "records[0].Name",
            customName: "trial_user_name",
            type: "string",
          },
          {
            name: "records[0].Company",
            customName: "trial_company",
            type: "string",
          },
          {
            name: "records[0].Plan_Tier__c",
            customName: "plan_tier",
            type: "string",
          },
          {
            name: "records[0].Owner.Email",
            customName: "ae_email",
            type: "string",
          },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Generate personalized onboarding guide based on plan and role",
        520,
        "You are a B2B SaaS onboarding specialist. Given the trial user's role, company, and plan tier, generate a personalized onboarding guide. Return JSON: setup_steps (string with 3 numbered steps), quick_win (string, first value-generating action), resource_link_description (string), personalized_tip (string).",
        "Trial user: {trial_user_name}. Company: {trial_company}. Title: {trial_user_title}. Plan: {plan_tier}. Generate a personalized onboarding guide.",
        [
          { field: "setup_steps", as: "setup_steps", type: "string" },
          { field: "quick_win", as: "quick_win", type: "string" },
          {
            field: "resource_link_description",
            as: "resource_desc",
            type: "string",
          },
          { field: "personalized_tip", as: "personal_tip", type: "string" },
        ],
      ),
      n("4", "condition", "Trial is on a team or enterprise plan?", 760, {
        conditionDetails: {
          rules: [
            { field: "plan_tier", operator: "notEquals", value: "individual" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Welcome + personalized setup guide with quick-win action",
        1000,
        "{trial_email}",
        "Welcome to your trial, {trial_user_name} — here's your personalized setup plan",
        "Hi {trial_user_name},\\n\\nWelcome! Your free trial is active and ready to go. Here's your personalized setup plan:\\n\\n📋 Your Setup Steps:\\n{setup_steps}\\n\\n⚡ Quick Win: {quick_win}\\nMost users who complete this in the first 24 hours see value within their first week.\\n\\n💡 Tip for you: {personal_tip}\\n\\n📚 {resource_desc}\\n\\n[Access Your Setup Guide →]\\n\\nQuestions? Reply to this email — we're here to help.\\n\\nBest,\\nThe Onboarding Team",
      ),
      n("6", "delayAction", "Wait 2 days for initial activation", 1240, {
        delayDetails: { duration: 2, unit: "days" },
      }),
      n("7", "condition", "Completed first activation milestone?", 1480, {
        conditionDetails: {
          rules: [
            {
              field: "activation_milestone_1",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Next milestone guidance or activation nudge",
        1720,
        "{trial_email}",
        "{trial_user_name}, great progress — here's your next milestone",
        "Hi {trial_user_name},\\n\\nGreat work getting started! You've completed your first setup milestone — here's what high-performing teams at companies like {trial_company} do next:\\n\\n🎯 Next Milestone: Connect your first integration to unlock automated workflows.\\n\\n[Set Up Your First Integration →]\\n\\n📊 Teams that complete this step are 3x more likely to see ROI within the trial period.\\n\\nNeed help? Our onboarding team offers free 15-minute setup sessions.\\n\\n[Book a Setup Session →]\\n\\nBest,\\nThe Onboarding Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Guides new trial users through activation milestones with a personalized onboarding sequence. Fetches account context from Salesforce, generates an AI-tailored setup guide based on role and plan tier, sends a welcome email with numbered setup steps and a quick-win action, waits 2 days for initial activation, then sends milestone-specific guidance or a re-engagement nudge to drive trial-to-paid conversion.",
  },

  // ─────────────────────────────────────────────────
  // 112. Trial Stalled Re-Engagement | Pattern L
  // ─────────────────────────────────────────────────
  "Trial Stalled Re-Engagement": {
    nodes: [
      trigger("Marketo: Trial user inactive for 5+ days", "webhook_app", {
        source: "marketo",
        event: "trial_inactivity_detected",
        howItStarts:
          "Fires when a trial user has not logged in or performed any product activity for 5 or more consecutive days, indicating a stalled trial that needs intervention to prevent silent churn.",
        setup:
          "In Marketo, create a Smart Campaign that runs daily checking for trial leads where Last_Login_Date__c is more than 5 days ago and Trial_Status__c = 'Active'. Webhook POSTs the stalled trial data.",
        inputFields: [],
      }),
      salesforceQueryNode(
        "2",
        "Salesforce: Fetch trial account health and usage context",
        280,
        "SELECT Id, Email, Name, Company, Trial_Start_Date__c, Last_Login_Date__c, Features_Used__c, Activation_Score__c, Owner.Email FROM Lead WHERE Email = '{trial_email}'",
        [
          { name: "records[0].Id", customName: "sf_lead_id", type: "string" },
          {
            name: "records[0].Name",
            customName: "trial_user_name",
            type: "string",
          },
          {
            name: "records[0].Company",
            customName: "trial_company",
            type: "string",
          },
          {
            name: "records[0].Features_Used__c",
            customName: "features_used",
            type: "string",
          },
          {
            name: "records[0].Activation_Score__c",
            customName: "activation_score",
            type: "number",
          },
          {
            name: "records[0].Owner.Email",
            customName: "ae_email",
            type: "string",
          },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Diagnose stall reason and recommend re-engagement strategy",
        520,
        "You are a B2B SaaS growth strategist. Given a stalled trial user's usage data, diagnose the likely stall reason and recommend re-engagement. Return JSON: stall_reason (string: setup_complexity|unclear_value|wrong_plan|time_constraint|competitor_eval), recommended_action (string), feature_to_highlight (string), personalized_message (string).",
        "Trial user: {trial_user_name} at {trial_company}. Days inactive: {days_inactive}. Features used: {features_used}. Activation score: {activation_score}/100. Trial day: {trial_day_number} of 14. Diagnose the stall and recommend re-engagement.",
        [
          { field: "stall_reason", as: "stall_reason", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "feature_to_highlight",
            as: "feature_highlight",
            type: "string",
          },
          {
            field: "personalized_message",
            as: "personal_message",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Trial has potential (activation score > 20)?", 760, {
        conditionDetails: {
          rules: [
            { field: "activation_score", operator: "greaterThan", value: "20" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Re-engage with targeted feature highlight and help offer",
        1000,
        "{trial_email}",
        "{trial_user_name}, don't miss this in your trial — most teams love it",
        "Hi {trial_user_name},\\n\\nNoticed you haven't been back in a few days — totally normal during a busy week!\\n\\n{personal_message}\\n\\n🌟 Feature Spotlight: {feature_highlight}\\nThis is the feature that gets teams like yours at {trial_company} to that \"aha\" moment fastest.\\n\\n[Try {feature_highlight} Now →]\\n\\nNeed help getting unstuck? Our team offers free 15-minute setup calls.\\n\\n[Book a Quick Setup Call →]\\n\\nYour trial has {days_remaining} days left — let's make them count!\\n\\nBest,\\nThe Onboarding Team",
      ),
      n("6", "delayAction", "Wait 5 days for re-engagement signals", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Trial user re-engaged (logged in or used feature)?",
        1480,
        {
          conditionDetails: {
            rules: [
              { field: "user_reengaged", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      slackNode(
        "8",
        "Slack: Alert AE — trial still stalled, needs personal outreach",
        1720,
        "⚠️ *Stalled Trial Alert*\\nUser: {trial_user_name} ({trial_email})\\nCompany: {trial_company}\\nDays inactive: {days_inactive}\\nActivation score: {activation_score}/100\\nStall reason: {stall_reason}\\nRecommended action: {recommended_action}\\nDays remaining: {days_remaining}\\n\\nAE: Please reach out with a personal call/email.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Rescues stalled trials by diagnosing the inactivity reason with AI based on feature usage and activation score, sending a targeted re-engagement email that highlights the most relevant unused feature, offering a setup call for users who may be stuck, waiting 5 days for re-engagement signals, and escalating to the assigned AE via Slack if the trial remains stalled so sales can provide personal outreach before the trial expires.",
  },

  // ─────────────────────────────────────────────────
  // 113. Demo Request Confirmation and Routing | Pattern K
  // ─────────────────────────────────────────────────
  "Demo Request Confirmation and Routing": {
    nodes: [
      trigger("Marketo: Demo request form submitted", "webhook_app", {
        source: "marketo",
        event: "form_fill_demo_request",
        howItStarts:
          "Fires when a prospect submits a demo request form on the website through Marketo, requiring immediate confirmation, intelligent routing to the right AE, and a prep email to set expectations.",
        setup:
          "In Marketo, create a Smart Campaign triggered by 'Fills Out Form' on the demo request landing page. The webhook POSTs the lead's email, name, company, role, use case, and preferred time.",
        inputFields: [],
      }),
      salesforceQueryNode(
        "2",
        "Salesforce: Enrich lead and determine territory/AE assignment",
        280,
        "SELECT Id, Name, Email, Company, Industry, AnnualRevenue, NumberOfEmployees, Owner.Name, Owner.Email, Territory__c FROM Lead WHERE Email = '{lead_email}'",
        [
          { name: "records[0].Id", customName: "sf_lead_id", type: "string" },
          {
            name: "records[0].Company",
            customName: "lead_company",
            type: "string",
          },
          {
            name: "records[0].Industry",
            customName: "lead_industry",
            type: "string",
          },
          {
            name: "records[0].AnnualRevenue",
            customName: "annual_revenue",
            type: "number",
          },
          {
            name: "records[0].Owner.Name",
            customName: "assigned_ae_name",
            type: "string",
          },
          {
            name: "records[0].Owner.Email",
            customName: "assigned_ae_email",
            type: "string",
          },
          {
            name: "records[0].Territory__c",
            customName: "territory",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "runCode",
        "Compute demo priority and assign appropriate AE",
        520,
        {
          codeDetails: {
            language: "javascript",
            code: `// Priority based on company size and industry fit
let priority = 'standard';
let slaHours = 24;
if (annual_revenue > 50000000 || lead_industry === 'Technology' || lead_industry === 'Financial Services') {
  priority = 'high';
  slaHours = 4;
}
if (annual_revenue > 500000000) {
  priority = 'enterprise';
  slaHours = 1;
}
const ae = assigned_ae_email || 'demo-queue@company.com';
const routing = territory ? territory + ' territory' : 'round-robin';
return { demo_priority: priority, sla_hours: slaHours, routed_ae: ae, routing_method: routing };`,
            outputs: [
              { name: "demo_priority", type: "string" },
              { name: "sla_hours", type: "number" },
              { name: "routed_ae", type: "string" },
              { name: "routing_method", type: "string" },
            ],
          },
        },
      ),
      n("4", "condition", "Enterprise or high-priority demo?", 760, {
        conditionDetails: {
          rules: [
            {
              field: "demo_priority",
              operator: "notEquals",
              value: "standard",
            },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Urgent alert — high-priority demo request for AE",
        1000,
        "🎯 *Demo Request — {demo_priority} priority*\\nProspect: {lead_name} ({lead_email})\\nCompany: {lead_company} | Industry: {lead_industry}\\nRevenue: ${annual_revenue} | Use case: {demo_use_case}\\nRouted to: {assigned_ae_name} ({routed_ae})\\nSLA: {sla_hours} hours\\nPreferred time: {preferred_demo_time}\\n\\n⚡ Route: {routing_method}",
      ),
      n("6", "delayAction", "Wait for AE SLA response window", 1240, {
        delayDetails: { duration: 4, unit: "hours" },
      }),
      salesforceUpdateNode(
        "7",
        "Salesforce: Update lead with demo request and routing info",
        1480,
        "Lead",
        "{sf_lead_id}",
        [
          { key: "Status", value: "Demo Requested" },
          { key: "Demo_Priority__c", value: "{demo_priority}" },
          { key: "Demo_Requested_Date__c", value: "{timestamp}" },
          { key: "Demo_Use_Case__c", value: "{demo_use_case}" },
        ],
        [{ name: "id", customName: "updated_lead_id", type: "string" }],
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log demo request routing for SLA analytics",
        1720,
        "Demo_Routing_Log",
        '[["{lead_email}","{lead_company}","{lead_industry}","{annual_revenue}","{demo_priority}","{routed_ae}","{routing_method}","{sla_hours}","{demo_use_case}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Handles demo requests end-to-end by enriching the lead in Salesforce, computing priority based on company revenue and industry fit, routing to the appropriate AE by territory with an SLA (1 hour for enterprise, 4 hours for high-priority), sending an urgent Slack alert for high-value requests, updating the lead record in Salesforce with demo request details, and logging every routing decision for SLA compliance and conversion analytics.",
  },
};
